import { useState, useEffect } from 'react';
import { Config } from '../types';
import { fetchConfig } from '../api';
import { ENVIRONMENT_COLORS, DEFAULT_ENV_COLOR } from '../utils';

interface HelpViewProps {
  onBack: () => void;
}

export function HelpView({ onBack }: HelpViewProps) {
  const [config, setConfig] = useState<Config | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await fetchConfig();
      setConfig(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load config');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="help-view">
      {/* Header */}
      <div className="help-header">
        <button className="back-btn" onClick={onBack}>
          &larr; Back
        </button>
        <h2>Help</h2>
      </div>

      {/* Content */}
      <div className="help-content">
        {/* Environment Color Key */}
        <section className="help-section">
          <h4>Environment Colors</h4>
          <p className="help-description">
            Each deployment row is color-coded by environment:
          </p>
          <div className="color-key">
            {ENVIRONMENT_COLORS.map((env) => (
              <div
                key={env.name}
                className="color-swatch"
                style={{
                  backgroundColor: env.bgColor,
                  color: env.textColor,
                }}
              >
                {env.name}
              </div>
            ))}
            <div
              className="color-swatch"
              style={{
                backgroundColor: DEFAULT_ENV_COLOR.bgColor,
                color: DEFAULT_ENV_COLOR.textColor,
              }}
            >
              Other
            </div>
          </div>
        </section>

        {/* App Types */}
        <section className="help-section">
          <h4>Application Types</h4>
          {loading && <p className="help-loading">Loading app types...</p>}
          {error && <p className="help-error">Error: {error}</p>}
          {!loading && !error && config && (
            <>
              <p className="help-description">
                Applications are categorized into the following types:
              </p>
              {config.appTypes.length === 0 ? (
                <p className="no-data">No app types configured</p>
              ) : (
                <table className="app-types-table">
                  <thead>
                    <tr>
                      <th>App Type</th>
                      <th>Hosting Platform</th>
                      <th>Framework</th>
                      <th>Deploy Method</th>
                    </tr>
                  </thead>
                  <tbody>
                    {config.appTypes.map((type) => (
                      <tr key={type.appType}>
                        <td>{type.appType}</td>
                        <td>{type.hostingPlatform}</td>
                        <td>{type.framework}</td>
                        <td>{type.deployMethod}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
